/**
 * BuildMode — shape the process flow. The canvas fills the area; a compact
 * Blocks / List toggle and an "Add step" button float in the top-right. Adding a
 * step opens a small dialog that asks where it goes (after which step, and
 * optionally before which) so it lands in the flow correctly. That placement
 * goes through the one shared store action (`addProcess`) the copilot uses too.
 * Selecting a node opens the NodeInspector beside it.
 */

import React, { useState } from 'react';
import ProcessDiagram from '../ProcessDiagram';
import ProcessTree from '../ProcessTree';
import NodeInspector from './NodeInspector';
import { useWorkspace } from './WorkspaceContext';

// Small modal: choose where the new step goes. Both selects are optional.
function AddStepDialog({ processes, defaultAfter, defaultBefore, onAdd, onCancel }) {
  const [after, setAfter] = useState(defaultAfter || '');
  const [before, setBefore] = useState(defaultBefore || '');

  const label = (p) => `${p.id} · ${(p.name || '').replace(/_/g, ' ')}`;
  const beforeOptions = processes.filter((p) => p.id !== after); // can't go before itself

  const submit = () => onAdd({ afterId: after || null, beforeId: before && before !== after ? before : null });

  return (
    <div className="naming-dialog-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="naming-dialog" style={{ maxWidth: 440 }}>
        <h3>Add a step</h3>
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', margin: '-8px 0 16px', lineHeight: 1.5 }}>
          Tell me where it goes so it links up correctly.
        </div>

        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Comes after</label>
          <select className="form-input" value={after} onChange={(e) => setAfter(e.target.value)}>
            <option value="">(start — no previous step)</option>
            {processes.map((p) => <option key={p.id} value={p.id}>{label(p)}</option>)}
          </select>
        </div>

        <div className="form-group" style={{ marginBottom: 8 }}>
          <label className="form-label">Goes before <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>(optional)</span></label>
          <select className="form-input" value={before} onChange={(e) => setBefore(e.target.value)}>
            <option value="">(end — no next step)</option>
            {beforeOptions.map((p) => <option key={p.id} value={p.id}>{label(p)}</option>)}
          </select>
        </div>

        <div className="naming-dialog-actions">
          <button onClick={onCancel}>Cancel</button>
          <button className="primary" onClick={submit}>Add step</button>
        </div>
      </div>
    </div>
  );
}

export default function BuildMode() {
  const {
    project, activeProcess, handleSelectProcess,
    leftViewMode, setLeftViewMode, handleAddProcess,
  } = useWorkspace();

  const [showAddDialog, setShowAddDialog] = useState(false);

  // "Blocks" is the diagram; anything else falls back to the list. (Table retired.)
  const view = leftViewMode === 'list' ? 'list' : 'diagram';

  const processes = Object.values(project?.processes || {}).sort((a, b) =>
    (a.id || '').localeCompare(b.id || '', undefined, { numeric: true }));

  const onAdd = (placement) => {
    setShowAddDialog(false);
    handleAddProcess(placement);
  };

  return (
    <div className="build-mode">
      <div className="build-main">
        <div className={`build-view-controls ${activeProcess ? 'inspector-open' : ''}`}>
          <div className="build-view-toggle">
            <button className={`bvt-btn ${view === 'diagram' ? 'active' : ''}`} onClick={() => setLeftViewMode('diagram')} title="Visual block diagram">Blocks</button>
            <button className={`bvt-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setLeftViewMode('list')} title="Compact list">List</button>
          </div>
          <button
            className="build-add-btn"
            data-coach="btn-add-process"
            onClick={() => setShowAddDialog(true)}
            title="Add a new step to the flow"
          >+ Add step</button>
        </div>

        <div className="left-panel-diagram" data-coach="panel-diagram">
          {view === 'diagram' ? (
            <ProcessDiagram project={project} selectedProcess={activeProcess} onSelectProcess={handleSelectProcess} />
          ) : (
            <ProcessTree project={project} selectedProcess={activeProcess} onSelectProcess={handleSelectProcess} />
          )}
        </div>
      </div>

      {activeProcess && <NodeInspector />}

      {showAddDialog && (
        <AddStepDialog
          processes={processes}
          defaultAfter={activeProcess?.id || ''}
          defaultBefore={activeProcess?.next_process_ids?.[0] || ''}
          onAdd={onAdd}
          onCancel={() => setShowAddDialog(false)}
        />
      )}
    </div>
  );
}
